import React, {useMemo} from "react";
import {ListItemText, ListSubheader, Radio} from "@mui/material";
import {Box, Checkbox, Chip, Colors, Text} from "@bounce/shared/ui";
import {MenuItem, Select} from "../../ui/components";
import {IconRadio, IconRadioChecked} from "../../assets";
import {ReachSegment} from "../../apis/requests/templates/types";
import {
  findSelectedExclusiveId,
  groupSegmentTags,
  nextSegmentSelection,
  selectableSegments,
} from "./segmentTagSelection";

const SELECTION_CONTROL_SX = {margin: "2px 12px 2px 6px", padding: 0};

type Props = {
  segments: ReachSegment[];
  value: number[];
  onChange: (segmentIds: number[]) => void;
  validationOnly: boolean;
};

const GroupHeader = ({title, hint, color}: {title: string; hint: string; color: string}) => (
  <ListSubheader sx={{lineHeight: "32px", backgroundColor: Colors.tertiary._100}}>
    <Box
      component="span"
      sx={{color, fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em"}}
    >
      {title}
    </Box>
    <Box component="span" sx={{color: Colors.grey._500, fontSize: 12, ml: 1}}>
      — {hint}
    </Box>
  </ListSubheader>
);

const SegmentTagsSelect = ({segments, value, onChange, validationOnly}: Props) => {
  const groups = useMemo(() => groupSegmentTags(segments, validationOnly), [segments, validationOnly]);
  const segmentById = useMemo(
    () => new Map(selectableSegments(groups).map((segment) => [segment.id, segment])),
    [groups]
  );
  const selectedExclusiveId = findSelectedExclusiveId(value, groups);
  const hasGeneralSelected = value.some((id) => id !== selectedExclusiveId && segmentById.has(id));
  const generalDisabled = selectedExclusiveId !== undefined;

  return (
    <Select
      multiple
      fullWidth
      size="small"
      displayEmpty
      value={value}
      onChange={(e) => onChange(nextSegmentSelection(value, e.target.value as number[], groups))}
      renderValue={(selected) => {
        const ids = (selected as number[]).filter((id) => segmentById.has(id));
        if (ids.length === 0)
          return (
            <Text size={14} sx={{color: Colors.grey._400}}>
              Select segments
            </Text>
          );
        return (
          <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.5}}>
            {ids.map((id) => {
              const segment = segmentById.get(id)!;
              if (segment.type === "EXCLUSIVE")
                return <Chip key={id} size_="small" variant_="info" label={`Exclusive · ${segment.name}`} />;
              return segment.type === "VALIDATION" ? (
                <Chip key={id} size_="small" variant_="inherit" label={`Validation · ${segment.name}`} />
              ) : (
                <Chip key={id} size_="small" variant_="blue" label={segment.name} />
              );
            })}
          </Box>
        );
      }}
    >
      {groups.exclusive.length > 0 && (
        <GroupHeader title="Exclusive" hint="targets this segment alone" color={Colors.grey._700} />
      )}
      {groups.exclusive.map((segment) => (
        <MenuItem key={segment.id} value={segment.id} disabled={hasGeneralSelected}>
          <Radio
            size="small"
            checked={selectedExclusiveId === segment.id}
            icon={<IconRadio width={16} height={16} color={Colors.grey._700} />}
            checkedIcon={<IconRadioChecked width={16} height={16} color={Colors.bw.black} />}
            sx={SELECTION_CONTROL_SX}
          />
          <ListItemText primary={segment.name} sx={{color: Colors.bw.black}} />
        </MenuItem>
      ))}
      {groups.exclusive.length > 0 && groups.general.length > 0 && (
        <GroupHeader title="General" hint="can be combined" color={Colors.grey._700} />
      )}
      {groups.general.map((segment) => (
        <MenuItem key={segment.id} value={segment.id} disabled={generalDisabled}>
          <Checkbox checked={value.includes(segment.id)} sx={SELECTION_CONTROL_SX} />
          <ListItemText primary={segment.name} />
        </MenuItem>
      ))}
      {groups.validation.map((segment) => (
        <MenuItem key={segment.id} value={segment.id}>
          <Checkbox checked={value.includes(segment.id)} sx={SELECTION_CONTROL_SX} />
          <ListItemText primary={segment.name} />
        </MenuItem>
      ))}
    </Select>
  );
};

export default SegmentTagsSelect;
